/**
 * Beyond Bohemian — Origin Map
 * Pure geographic asset. Reads one product metafield:
 *   - custom.country_of_origin → "Region, Country" (e.g. "Boeny, Madagascar")
 *
 * Pin lands at country centroid (Natural Earth data via d3.geoCentroid).
 * Region/country label appears above the pin.
 *
 * The sourcing-pathway card on the product page is separate and renders
 * its own pathway pill, description, and Sourcing Standards link.
 */
(function () {
  'use strict';

  const CSS = `
    .bb-origin-map {
      --bb-ink: #1F1B16;
      --bb-ink-2: #4A3E29;
      --bb-soft: #9A8F7F;
      --bb-card: #FDFBF6;
      --bb-card-alt: #F3EBD6;
      --bb-hairline: #C9BDA3;
      --bb-accent: #7A6B4F;
      --bb-ff-sans: 'Karla', system-ui, sans-serif;
      background: var(--bb-card-alt);
      border: 1px solid var(--bb-hairline);
      border-radius: 2px;
      color: var(--bb-ink);
      font-family: var(--bb-ff-sans);
      overflow: hidden;
      max-width: 760px;
      margin: 0 auto;
      box-sizing: border-box;
    }
    .bb-origin-map *, .bb-origin-map *::before, .bb-origin-map *::after { box-sizing: border-box; }
    .bb-origin-map__svg { width: 100%; height: auto; display: block; }
    .bb-origin-map__loading {
      height: 280px;
      display: flex; align-items: center; justify-content: center;
      font-family: var(--bb-ff-sans);
      font-size: 11px; letter-spacing: .18em;
      color: var(--bb-soft); text-transform: uppercase;
    }
  `;

  function injectStyles() {
    if (document.getElementById('bb-origin-map-css')) return;
    const style = document.createElement('style');
    style.id = 'bb-origin-map-css';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const COUNTRY_ALIASES = {
    'USA': 'United States of America',
    'US': 'United States of America',
    'United States': 'United States of America',
    'UK': 'United Kingdom',
    'Burma': 'Myanmar',
    'Czech Republic': 'Czechia',
    'Ivory Coast': "Côte d'Ivoire",
    'Congo': 'Dem. Rep. Congo',
    'DRC': 'Dem. Rep. Congo',
    'Democratic Republic of Congo': 'Dem. Rep. Congo',
    'Democratic Republic of the Congo': 'Dem. Rep. Congo',
    'South Korea': 'Korea',
    'Korea, South': 'Korea'
  };

  /* ---- region gazetteer -------------------------------------------------
     The country_of_origin metafield is "Region, Country", but pinning at the
     COUNTRY centroid puts New Mexico in Wyoming (the USA centroid includes
     Alaska). Region centroids for every value in the catalog, 2026-08-20.
     TWIN: this table also lives in bb-origin-map.js / bb-quickshop.js —
     keep both in sync when regions are added. Keys are normalized by
     bbNormRegion(): lowercase, diacritics stripped, trailing
     province/region/district dropped. Falls back to the country centroid
     (with a contiguous-US override) when a region is missing. */
  var BB_REGION_COORDS = {
    /* Madagascar */
    'atsimo-andrefana':{lat:-22.9,lng:44.3}, 'amboasitra':{lat:-20.53,lng:47.24},
    'alaotra-mangoro':{lat:-17.8,lng:48.4}, 'boeny':{lat:-16.3,lng:46.5},
    'bongolava':{lat:-18.8,lng:45.6}, 'mahajanga':{lat:-15.72,lng:46.32},
    'ambatondrazaka':{lat:-17.83,lng:48.42}, 'sofia':{lat:-15.5,lng:48.0},
    'vakinankaratra':{lat:-19.75,lng:46.9}, "amoron'i mania":{lat:-20.5,lng:47.2},
    'analamanga':{lat:-18.7,lng:47.5}, 'anosy':{lat:-24.8,lng:46.9},
    'betsiboka':{lat:-17.0,lng:46.8}, 'sakoany':{lat:-15.95,lng:45.95},
    'akansobe':{lat:-18.3,lng:47.1}, 'androy':{lat:-24.8,lng:45.7},
    'antsiranana':{lat:-12.3,lng:49.3}, 'atsinanana':{lat:-18.7,lng:48.9},
    'toliara':{lat:-23.35,lng:43.67},
    /* Brazil */
    'minas gerais':{lat:-18.5,lng:-44.4}, 'rio grande do sul':{lat:-29.8,lng:-53.2},
    'bahia':{lat:-12.6,lng:-41.7}, 'parana':{lat:-24.6,lng:-51.5},
    'paraiba':{lat:-7.1,lng:-36.7},
    /* Zimbabwe */
    'masvingo':{lat:-20.1,lng:30.8}, 'mashonaland west':{lat:-17.5,lng:29.9},
    'mashonaland central':{lat:-16.8,lng:31.1}, 'mashonaland east':{lat:-18.1,lng:31.8},
    'matabeleland south':{lat:-21.0,lng:29.0}, 'midlands':{lat:-19.0,lng:29.8},
    'chivhu':{lat:-19.02,lng:30.9},
    /* Zambia */
    'southern':{lat:-16.5,lng:27.0}, 'copperbelt':{lat:-13.0,lng:28.0},
    /* Peru */
    'ica':{lat:-14.1,lng:-75.6}, 'arequipa':{lat:-15.8,lng:-72.3},
    'ancash':{lat:-9.4,lng:-77.6}, 'huarochiri':{lat:-11.9,lng:-76.4},
    'junin':{lat:-11.5,lng:-75.0}, 'lima':{lat:-12.0,lng:-76.8},
    'acari':{lat:-15.43,lng:-74.62},
    /* United States */
    'arkansas':{lat:34.8,lng:-92.4}, 'arizona':{lat:34.3,lng:-111.7},
    'san carlos apache reservation, arizona':{lat:33.4,lng:-110.4},
    'utah':{lat:39.3,lng:-111.7}, 'south dakota':{lat:44.4,lng:-100.3},
    'new mexico':{lat:34.4,lng:-106.1},
    /* Namibia */
    'erongo':{lat:-21.8,lng:15.5}, 'kunene':{lat:-19.5,lng:13.8},
    'karas':{lat:-27.0,lng:17.5},
    /* South Africa */
    'northern cape':{lat:-29.5,lng:21.5}, 'limpopo':{lat:-23.8,lng:29.5},
    'mpumalanga':{lat:-25.8,lng:30.3},
    /* DR Congo + Republic of the Congo */
    'haut-lomami':{lat:-8.0,lng:25.5}, 'haut-katanga':{lat:-10.7,lng:27.5},
    'reneville':{lat:-4.3,lng:14.4},
    /* Malawi / Indonesia / China / Russia */
    'nsipe':{lat:-14.9,lng:34.75}, 'west java':{lat:-6.9,lng:107.5},
    'jiangxi':{lat:27.3,lng:115.7}, 'republic of karelia':{lat:63.5,lng:33.0},
    /* Americas */
    'artigas':{lat:-30.6,lng:-56.9}, 'boyaca':{lat:5.7,lng:-73.1},
    'salta':{lat:-24.8,lng:-65.4}, 'catamarca':{lat:-27.3,lng:-66.9},
    'rio negro':{lat:-40.4,lng:-67.2}, 'jalisco':{lat:20.5,lng:-103.6},
    'chihuahua':{lat:28.5,lng:-106.0}, 'barahona':{lat:18.2,lng:-71.1},
    'angel sandoval':{lat:-17.0,lng:-58.8}, 'quebec':{lat:52.0,lng:-71.0},
    /* Africa (rest) + Oceania */
    'hhohho':{lat:-26.0,lng:31.3}, 'huila':{lat:-14.9,lng:14.9},
    'francistown':{lat:-21.2,lng:27.5}, 'central':{lat:-21.5,lng:26.5},
    'bobonong':{lat:-21.97,lng:28.43}, 'nampula':{lat:-15.1,lng:39.0},
    'tete':{lat:-15.6,lng:32.7}, 'zambezia':{lat:-16.5,lng:37.0},
    'morogoro':{lat:-6.8,lng:37.7}, 'western australia':{lat:-25.5,lng:122.0}
  };
  /* geoCentroid of a multipolygon USA lands in the Alaska-weighted north;
     pin the contiguous states instead when no region matched */
  var BB_COUNTRY_COORDS = {
    'united states of america':{lat:39.8,lng:-98.6}
  };
  function bbNormRegion(s){
    return (s || '').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u01c1\u2016]/g, '')
      .replace(/[\u2019\u2018]/g, "'")
      .replace(/\s+(province|region|district)$/, '')
      .trim();
  }

  function parseOrigin(raw) {
    if (!raw) return null;
    // Split on comma, em-dash, or en-dash ONLY. Hyphens are preserved.
    const parts = raw.split(/\s*[,—–]\s*/).map(p => p.trim()).filter(Boolean);
    if (parts.length === 0) return null;
    if (parts.length === 1) {
      const country = parts[0];
      return { region: '', country: COUNTRY_ALIASES[country] || country };
    }
    const country = parts[parts.length - 1];
    const region = parts.slice(0, -1).join(', ');
    return { region, country: COUNTRY_ALIASES[country] || country };
  }

  let atlasPromise = null;
  function loadAtlas() {
    if (atlasPromise) return atlasPromise;
    const loadScript = (src, globalName) => new Promise((resolve, reject) => {
      if (window[globalName]) return resolve();
      const s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = () => reject(new Error('Failed to load ' + src));
      document.head.appendChild(s);
    });
    atlasPromise = (async () => {
      await loadScript('https://cdn.jsdelivr.net/npm/topojson-client@3/dist/topojson-client.min.js', 'topojson');
      await loadScript('https://cdn.jsdelivr.net/npm/d3@7/dist/d3.min.js', 'd3');
      const res = await fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json');
      const data = await res.json();
      return window.topojson.feature(data, data.objects.countries);
    })();
    return atlasPromise;
  }

  async function render(el) {
    const originRaw = el.dataset.origin || '';
    const parsed = parseOrigin(originRaw);
    if (!parsed) return;
    const { region, country } = parsed;

    injectStyles();

    el.innerHTML = `<div class="bb-origin-map__loading">Loading atlas…</div>`;

    try {
      const countries = await loadAtlas();
      const feature = countries.features.find(f => f.properties.name === country);
      if (!feature) {
        console.warn('[bb-origin-map] Country not found in atlas:', country,
          '— add an alias to COUNTRY_ALIASES in bb-origin-map.js');
      }
      /* region centroid first (2026-08-20: the country centroid put New
         Mexico's pin in Wyoming — the USA centroid is Alaska-weighted);
         then the contiguous-US override; the atlas centroid is last */
      const rc = BB_REGION_COORDS[bbNormRegion(region)] || BB_COUNTRY_COORDS[country.toLowerCase()];
      const centroid = rc ? [rc.lng, rc.lat]
                          : (feature ? window.d3.geoCentroid(feature) : [0, 0]);
      el.innerHTML = buildSvg(countries, country, region, centroid);
      attachHover(el);
    } catch (e) {
      console.error('[bb-origin-map]', e);
      el.innerHTML = '<div class="bb-origin-map__loading">Map unavailable</div>';
    }
  }

  function buildSvg(countries, country, region, centroid) {
    const width = 720, height = 380;
    const projection = window.d3.geoNaturalEarth1()
      .fitExtent([[12, 12], [width - 12, height - 12]], { type: 'Sphere' });
    const path = window.d3.geoPath(projection);
    const pin = projection(centroid);
    const graticule = window.d3.geoGraticule10();

    const countryPaths = countries.features.map(f => {
      const isOrigin = f.properties.name === country;
      return `<path
        d="${path(f)}"
        data-name="${escapeAttr(f.properties.name)}"
        fill="${isOrigin ? '#7A6B4F' : '#E4DBC4'}"
        stroke="${isOrigin ? '#4A3E29' : '#C9BDA3'}"
        stroke-width="${isOrigin ? 0.7 : 0.35}"
        style="transition: fill .15s ease"
      />`;
    }).join('');

    const pinLabel = (region || country).toUpperCase();
    const labelWidth = Math.max(80, pinLabel.length * 7 + 16);
    const pinGroup = pin ? `
      <g>
        <circle cx="${pin[0]}" cy="${pin[1]}" r="28" fill="url(#bbPinGlow)"/>
        <circle cx="${pin[0]}" cy="${pin[1]}" r="6" fill="#FDFBF6" stroke="#4A3E29" stroke-width="1.4"/>
        <circle cx="${pin[0]}" cy="${pin[1]}" r="2.5" fill="#4A3E29"/>
        <line x1="${pin[0]}" y1="${pin[1] - 6}" x2="${pin[0]}" y2="${pin[1] - 18}" stroke="#4A3E29" stroke-width="1" stroke-dasharray="2 2"/>
        <rect x="${pin[0] - labelWidth/2}" y="${pin[1] - 38}" width="${labelWidth}" height="18" rx="2" fill="#FDFBF6" stroke="#C9BDA3" stroke-width="0.6"/>
        <text x="${pin[0]}" y="${pin[1] - 25}" text-anchor="middle" font-family="Karla, sans-serif" font-size="10" fill="#4A3E29" font-weight="600" letter-spacing="0.1em">${escapeAttr(pinLabel)}</text>
      </g>
    ` : '';

    return `
      <svg viewBox="0 0 ${width} ${height}" class="bb-origin-map__svg" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="bbPinGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#7A6B4F" stop-opacity=".45"/>
            <stop offset="100%" stop-color="#7A6B4F" stop-opacity="0"/>
          </radialGradient>
        </defs>
        <path d="${path({ type: 'Sphere' })}" fill="rgb(65, 62, 70)" stroke="#C9BDA3" stroke-width="0.6"/>
        <path d="${path(graticule)}" fill="none" stroke="#D8CDB4" stroke-width="0.35" opacity=".55"/>
        ${countryPaths}
        ${pinGroup}
        <text class="bb-origin-map__hover-label" x="12" y="${height - 14}" font-family="Karla, sans-serif" font-size="10" fill="#6B6258" letter-spacing="0.14em" style="display:none"></text>
      </svg>
    `;
  }

  function attachHover(el) {
    const svg = el.querySelector('svg');
    if (!svg) return;
    const label = svg.querySelector('.bb-origin-map__hover-label');
    svg.querySelectorAll('path[data-name]').forEach(p => {
      p.addEventListener('mouseenter', () => {
        const isOrigin = p.getAttribute('fill') === '#7A6B4F';
        if (!isOrigin) p.setAttribute('fill', '#D8CDB4');
        label.textContent = (p.dataset.name || '').toUpperCase();
        label.style.display = '';
      });
      p.addEventListener('mouseleave', () => {
        const isOrigin = p.getAttribute('stroke-width') === '0.7';
        if (!isOrigin) p.setAttribute('fill', '#E4DBC4');
        label.style.display = 'none';
      });
    });
  }

  function escapeAttr(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }

  function mountAll(root) {
    (root || document).querySelectorAll('.bb-origin-map:not([data-bb-mounted])').forEach(el => {
      el.setAttribute('data-bb-mounted', '1');
      render(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll());
  } else {
    mountAll();
  }

  window.BBOriginMap = { mountAll, render, parseOrigin };
})();
